// Formatting helpers for the admin page. Dates use the browser locale; money
// comes from Stripe in minor units (cents/bani).

const DATE = { year: 'numeric', month: 'short', day: 'numeric' };
const DATE_TIME = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };

export function fmtDate(value, fallback = '—') {
    if (!value) {
        return fallback;
    }
    const d = new Date(value);
    return isNaN(d.getTime()) ? fallback : d.toLocaleDateString(undefined, DATE);
}

export function fmtDateTime(value, fallback = '—') {
    if (!value) {
        return fallback;
    }
    const d = new Date(value);
    return isNaN(d.getTime()) ? fallback : d.toLocaleString(undefined, DATE_TIME);
}

export function fmtNumber(value) {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    const n = Number(value);
    return isNaN(n) ? String(value) : n.toLocaleString();
}

export function fmtMoney(minorUnits, currency) {
    if (minorUnits === null || minorUnits === undefined || minorUnits === '') {
        return '—';
    }
    const amount = Number(minorUnits) / 100;
    if (isNaN(amount)) {
        return '—';
    }
    if (!currency) {
        return amount.toFixed(2);
    }
    try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() }).format(amount);
    } catch (e) {
        return `${amount.toFixed(2)} ${currency.toUpperCase()}`;
    }
}

// Postgres intervals arrive parsed as { days, hours, minutes, seconds, milliseconds }.
export function intervalToMs(interval) {
    if (!interval || typeof interval !== 'object') {
        return 0;
    }
    return (
        (interval.days || 0) * 86400000 +
        (interval.hours || 0) * 3600000 +
        (interval.minutes || 0) * 60000 +
        (interval.seconds || 0) * 1000 +
        (interval.milliseconds || 0)
    );
}

export function fmtInterval(interval) {
    const ms = intervalToMs(interval);
    if (!ms) {
        return '—';
    }
    const totalSeconds = Math.round(ms / 1000);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    const parts = [];
    if (h) {
        parts.push(`${h} h`);
    }
    if (m) {
        parts.push(`${m} min`);
    }
    if (s || parts.length === 0) {
        parts.push(`${s} sec`);
    }
    return parts.join(' ');
}

// A lesson row is live when its heartbeat end is still in the future. Session
// rows only carry start + average duration, so fall back to "ended within 60 s".
export function isLessonLive(row) {
    if (!row) {
        return false;
    }
    const now = Date.now();
    if (row.endtime) {
        return new Date(row.endtime).getTime() > now;
    }
    if (row.starttime && row.duration) {
        const ended = new Date(row.starttime).getTime() + intervalToMs(row.duration);
        return now - ended < 60000;
    }
    return false;
}

export function fullName(row) {
    if (!row) {
        return '';
    }
    return [row.first_name, row.last_name].filter(Boolean).join(' ');
}

// yyyy-mm-dd in local time, for the lessons date filter.
export function toDateParam(date) {
    if (!date || isNaN(date.getTime())) {
        return '';
    }
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

// The admin API answers bad requests with { error: 'why' }; surface that text to the admin.
export function errorMessage(err, fallback = 'The request failed. Check the server log.') {
    return err?.response?.data?.error || fallback;
}
